import { WalletBalance } from '@/components/billing/WalletBalance'
import { cardBrandName } from '@/components/payment-methods/PaymentMethodsList'
import { centsToEuros } from '@/constants'
import { useJobillaOrgId } from '@/hooks'
import { useAuth } from '@/lib/auth'
import { PaymentAuthorisationError } from '@/lib/billing/authorise-payment'
import {
	type BillingProfileData,
	campaignChannelMethods,
	type CardPaymentMethodDetails,
	type PaymentMethodData,
	preferredPaymentMethod,
	type SubmitCampaignForReviewData,
	useCampaignAddonShow,
	useCampaignInvoiceStatus,
	useCampaignsShow,
	useCustomerValidateBillingDetails,
	useCustomerWalletShow,
	useInventoryIndex,
	useInvoiceSettingsShow,
	usePaymentMethodIndex,
	useProfilesIndex,
} from '@jobilla/nexus-sdk'
import { ChevronDownIcon } from 'lucide-react'
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '../../rapid-ui'
import { type CampaignChannel } from '../../views/shared/conformed/review'
import { allocateInventory, type InventorySelection, isSelectableInventoryItem } from './inventory-allocation'
import { InventoryModal } from './inventory-modal'

/**
 * Tagged option presented in the payment-method selector. Cards each have
 * their own option (one per saved card), and `'invoice'` is added when
 * invoicing is enabled for the organisation.
 */
export type PaymentOption =
	| {
		kind: 'card'
		methodId: string
		brand: string
		last4: string
		nickname: string | null
		isDefault: boolean
		expMonth: number
		expYear: number
	}
	| { kind: 'invoice' }

export type BillingDrawerState = {
	selected: PaymentOption | null
	amountRemainingCents: number
	requiresPayment: boolean
	externalInvoicing: boolean
	paymentMethods: SubmitCampaignForReviewData['paymentMethods']
	/** Billing profile to charge; null when profiles couldn't be loaded, which bills the primary. */
	profileId: string | null
	inventoryItems: NonNullable<SubmitCampaignForReviewData['inventoryItems']>
}

const isCardDetails = (details: PaymentMethodData['details']): details is CardPaymentMethodDetails => 'brand' in details

function paymentOptionKey(option: PaymentOption): string {
	return option.kind === 'card' ? `card:${option.methodId}` : 'invoice'
}

function paymentOptionLabel(option: PaymentOption, t: (key: string, opts?: Record<string, unknown>) => string): string {
	if (option.kind === 'invoice') {
		return t('billingDrawer.invoiceOption')
	}
	if (option.nickname) {
		return t('billingDrawer.cardOptionNicknamed', { nickname: option.nickname, last4: option.last4 })
	}
	return t('billingDrawer.cardOption', { last4: option.last4, brand: cardBrandName(option.brand) })
}

export type BillingDrawerProps = {
	campaignId: string
	channels: CampaignChannel[]
	onStateChange?: (state: BillingDrawerState) => void
	onConfirm: () => Promise<void>
}

export function BillingDrawer(
	{ campaignId, channels, onStateChange, onConfirm }: BillingDrawerProps,
) {
	const { t, i18n } = useTranslation('editor')
	const organisationId = useJobillaOrgId()
	const { can } = useAuth()
	const currencyFormatter = useMemo(
		() => new Intl.NumberFormat(i18n.language, { style: 'currency', currency: 'EUR' }),
		[i18n.language],
	)
	const [selectedKey, setSelectedKey] = useState<string | null>(null)
	const [selectedProfileId, setSelectedProfileId] = useState<string | null>(null)
	const [useWallet, setUseWallet] = useState(true)
	const [inventorySelection, setInventorySelection] = useState<InventorySelection>({})
	const [isInventoryModalOpen, setIsInventoryModalOpen] = useState(false)
	const [isSubmitting, setIsSubmitting] = useState(false)
	const [submitError, setSubmitError] = useState<string | null>(null)

	const campaign = useCampaignsShow(campaignId)
	const campaignAddons = useCampaignAddonShow(campaignId)
	const invoiceStatus = useCampaignInvoiceStatus(organisationId, campaignId, {
		enabled: !!organisationId,
	})

	const profilesQuery = useProfilesIndex(organisationId, {
		enabled: !!organisationId && can('profile:view'),
	})
	const profiles = useMemo(() => profilesQuery.data?.data ?? [], [profilesQuery.data])
	// Only offer a choice when there's genuinely more than one profile.
	const showProfileChooser = profiles.length > 1
	const selectedProfile = profiles.find((profile) => profile.id === selectedProfileId) ?? null

	// Default the selection to the primary profile once profiles have loaded.
	useEffect(() => {
		if (profiles.length === 0 || selectedProfileId !== null) {
			return
		}
		const primary = profiles.find((profile) => profile.isPrimary)
		setSelectedProfileId((primary ?? profiles[0]!).id)
	}, [profiles, selectedProfileId])

	// Scope the breakdown's VAT/card to the chosen profile's Stripe customer.
	const billingValidation = useCustomerValidateBillingDetails(
		organisationId,
		selectedProfileId ? { profile: selectedProfileId } : undefined,
		{ enabled: !!organisationId },
	)
	const wallet = useCustomerWalletShow(organisationId, {
		enabled: !!organisationId,
	})
	const invoiceSettings = useInvoiceSettingsShow(organisationId, {
		enabled: !!organisationId,
	})
	const paymentMethodsList = usePaymentMethodIndex(
		organisationId,
		selectedProfileId ? { profileId: selectedProfileId } : undefined,
		{ enabled: !!organisationId && can('payment_method:view') },
	)

	const budget = campaign.data?.budget ? centsToEuros(campaign.data.budget) : 0
	const vatPercent = billingValidation.data?.customer?.tax?.taxRate?.percentage ?? 0
	const walletCredits = centsToEuros(wallet.data?.balance ?? 0)
	const paymentTerms = billingValidation.data?.customer?.paymentOverview?.paymentTerms ?? null
	const invoicingEnabled = invoiceSettings.data?.canPayViaInvoice ?? false
	const externalInvoicing = invoiceSettings.data?.externalInvoicing ?? false
	const isDemo = invoiceSettings.data?.isDemo ?? false
	const skipsPayment = externalInvoicing || isDemo
	const isBillingComplete = billingValidation.data?.validation.valid ?? false

	const canUseInventory = can('inventory:view') && !skipsPayment
	const inventoryQuery = useInventoryIndex(organisationId, {
		enabled: !!organisationId && canUseInventory,
	})
	const selectableInventory = useMemo(
		() => (inventoryQuery.data?.data ?? []).filter(isSelectableInventoryItem),
		[inventoryQuery.data],
	)
	const cardOptions = useMemo<PaymentOption[]>(() => {
		const cards = (paymentMethodsList.data?.data ?? [])
			.filter((pm) => isCardDetails(pm.details))
			// A card can only fund a campaign billed to its own profile.
			.filter((pm) => !selectedProfile || pm.stripeCustomerId === selectedProfile.stripeCustomerId)
		// Default cards first, then everything else preserving the API order.
		const sorted = [...cards].sort((a, b) => Number(b.isDefault) - Number(a.isDefault))
		return sorted.map((pm) => {
			const details = pm.details as CardPaymentMethodDetails
			return {
				kind: 'card' as const,
				methodId: pm.id,
				brand: details.brand,
				last4: details.last4,
				nickname: pm.nickname,
				isDefault: pm.isDefault,
				expMonth: details.expMonth,
				expYear: details.expYear,
			}
		})
	}, [paymentMethodsList.data, selectedProfile])

	const invoiceIsPreferred = selectedProfile?.preferredPaymentMethod === preferredPaymentMethod.invoice

	const paymentOptions = useMemo<PaymentOption[]>(() => {
		if (!invoicingEnabled) {
			return cardOptions
		}
		const invoiceOption: PaymentOption = { kind: 'invoice' }
		return invoiceIsPreferred ? [invoiceOption, ...cardOptions] : [...cardOptions, invoiceOption]
	}, [cardOptions, invoicingEnabled, invoiceIsPreferred])

	const selectedAddons = useMemo(() => {
		return (campaignAddons.data?.addons ?? []).filter((addon) => addon.selected && addon.price != null)
	}, [campaignAddons.data?.addons])

	const addonsTotal = useMemo(() => {
		return selectedAddons.reduce((sum, addon) => sum + centsToEuros(addon.price!), 0)
	}, [selectedAddons])

	const campaignCostCents = (campaign.data?.budget ?? 0)
		+ selectedAddons.reduce((sum, addon) => sum + addon.price!, 0)
	const inventoryAllocation = useMemo(
		() => allocateInventory(selectableInventory, inventorySelection, campaignCostCents),
		[selectableInventory, inventorySelection, campaignCostCents],
	)
	const walletTopUp = centsToEuros(inventoryAllocation.topUpCents)
	const inventoryItems = useMemo<BillingDrawerState['inventoryItems']>(
		() => inventoryAllocation.lines.map((line) => ({ id: line.item.id, quantity: line.quantity })),
		[inventoryAllocation.lines],
	)

	const subtotal = budget + addonsTotal + walletTopUp
	const vatAmount = subtotal * (vatPercent / 100)
	const total = subtotal + vatAmount

	const successfulPayments = useMemo(() => {
		return (invoiceStatus.data?.payments ?? []).filter((payment) => payment.status === 'paid')
	}, [invoiceStatus.data?.payments])
	const totalPayments = useMemo(() => {
		return successfulPayments.reduce((sum, payment) => sum + centsToEuros(payment.amount), 0)
	}, [successfulPayments])

	const remainingAfterPayments = Math.max(0, total - totalPayments)
	const isInventoryApplied = inventoryAllocation.appliedCents > 0
	const walletAvailable = !skipsPayment && walletCredits > 0 && remainingAfterPayments > 0
	const walletApplicable = walletAvailable && !isInventoryApplied
		? Math.min(walletCredits, remainingAfterPayments)
		: 0
	const walletApplied = useWallet ? walletApplicable : 0
	const amountRemaining = Math.max(0, remainingAfterPayments - walletApplied)
	const requiresPayment = !skipsPayment && amountRemaining > 0
	const walletCoversAll = useWallet && walletApplicable > 0 && walletCredits >= remainingAfterPayments

	const selected = useMemo<PaymentOption | null>(() => {
		if (selectedKey === null) { return null }
		return paymentOptions.find((option) => paymentOptionKey(option) === selectedKey) ?? null
	}, [paymentOptions, selectedKey])

	const hasBillableChannel = channels.some((channel) => campaignChannelMethods.isBillable(channel))

	const canPay = !hasBillableChannel
		|| skipsPayment
		|| !requiresPayment
		|| selected?.kind === 'card'
		|| (selected?.kind === 'invoice' && isBillingComplete)

	const paymentErrorMessage = (error: unknown): string => {
		if (error instanceof PaymentAuthorisationError) {
			switch (error.reason) {
				case 'declined':
					return t('billingDrawer.paymentDeclined')
				case 'three_ds_failed':
					return t('billingDrawer.paymentAuthenticationFailed')
				case 'stripe_unavailable':
					return t('billingDrawer.paymentUnavailable')
			}
		}
		return t('billingDrawer.paymentFailed')
	}

	const handleConfirm = async () => {
		setSubmitError(null)
		setIsSubmitting(true)
		try {
			await onConfirm()
		} catch (error) {
			setSubmitError(paymentErrorMessage(error))
			setIsSubmitting(false)
		}
	}

	// Tracks whether the user has explicitly picked a payment option. While
	// false, the selection auto-follows `paymentOptions[0]` (the profile's
	// primary payment method) so when payment-methods
	// load asynchronously the default doesn't stick to the only-invoice
	// fallback that was visible during loading.
	const userPickedRef = useRef(false)

	const handlePickPaymentOption = (option: PaymentOption) => {
		userPickedRef.current = true
		setSelectedKey(paymentOptionKey(option))
		setSubmitError(null)
	}

	useEffect(() => {
		if (!requiresPayment || paymentOptions.length === 0) {
			userPickedRef.current = false
			if (selectedKey !== null) {
				setSelectedKey(null)
			}
			return
		}
		const preferredKey = paymentOptionKey(paymentOptions[0]!)
		if (userPickedRef.current) {
			// Honour the user's choice unless it has disappeared from the options.
			if (selectedKey === null || !paymentOptions.some((option) => paymentOptionKey(option) === selectedKey)) {
				userPickedRef.current = false
				setSelectedKey(preferredKey)
			}
			return
		}
		// App-managed default: always mirror the preferred first option.
		if (selectedKey !== preferredKey) {
			setSelectedKey(preferredKey)
		}
	}, [requiresPayment, paymentOptions, selectedKey])

	const paymentMethods = useMemo<SubmitCampaignForReviewData['paymentMethods']>(() => {
		if (skipsPayment) {
			return [{ type: 'invoice' }]
		}

		const methods: SubmitCampaignForReviewData['paymentMethods'] = []

		if (useWallet && walletApplied > 0) {
			methods.push({ type: 'wallet', amount: Math.round(walletApplied * 100) })
		}

		if (requiresPayment && selected?.kind === 'invoice') {
			methods.push({ type: 'invoice' })
		}

		if (requiresPayment && selected?.kind === 'card') {
			// Stripe PaymentIntent wiring is not yet built — the backend will reject
			// this with `payment_method_intent_required` until it is. The selected
			// card id (`selected.methodId`) is the one we'll create the intent against.
			methods.push({ type: 'stripe', amount: Math.round(amountRemaining * 100) })
		}

		return methods
	}, [skipsPayment, useWallet, walletApplied, requiresPayment, selected, amountRemaining])

	useEffect(() => {
		onStateChange?.({
			selected,
			amountRemainingCents: Math.round(amountRemaining * 100),
			requiresPayment,
			externalInvoicing,
			paymentMethods,
			profileId: selectedProfileId,
			inventoryItems,
		})
	}, [
		selected,
		amountRemaining,
		requiresPayment,
		externalInvoicing,
		paymentMethods,
		selectedProfileId,
		inventoryItems,
		onStateChange,
	])

	const isLoading = campaign.isPending
		|| campaignAddons.isPending
		|| invoiceStatus.isPending
		|| billingValidation.isPending
		|| wallet.isPending
		|| invoiceSettings.isPending
		|| (can('payment_method:view') && paymentMethodsList.isPending)
		|| (can('profile:view') && profilesQuery.isPending)
		|| (canUseInventory && !!organisationId && inventoryQuery.isPending)

	const chromeClasses =
		'w-[340px] bg-white border border-grey-100 border-l-0 rounded-r-md p-6 shadow-[6px_0_24px_-8px_rgba(15,23,42,0.10)] flex flex-col gap-4'

	if (isLoading) {
		return (
			<aside className={`${chromeClasses} animate-pulse`}>
				<div className="h-4 w-24 bg-grey-100 rounded" />
				<div className="h-4 w-32 bg-grey-100 rounded" />
				<div className="h-4 w-28 bg-grey-100 rounded" />
				<div className="border-t border-grey-100 my-1" />
				<div className="h-4 w-20 bg-grey-200 rounded" />
				<div className="h-4 w-24 bg-grey-200 rounded" />
			</aside>
		)
	}

	const buttonHeading = (requiresPayment || walletCoversAll)
		? t('billingDrawer.confirmAndPay')
		: t('billingDrawer.confirm')

	return (
		<aside className={`${chromeClasses} text-sm`}>
			<div className="flex items-start justify-between gap-4">
				<div className="text-base font-semibold text-grey-900 mt-1">
					{t('billingDrawer.heading')}
				</div>
				{wallet.data && !skipsPayment && (
					<div className="text-right shrink-0">
						<div className="text-xs font-medium text-grey-500">
							{t('common:settings.invoices.pay.walletBalanceLabel')}
						</div>
						<WalletBalance
							balanceCents={wallet.data.balance}
							taxPercentage={vatPercent}
							currencyFormatter={currencyFormatter}
							className="text-base font-semibold text-grey-900 tabular-nums"
						/>
					</div>
				)}
			</div>

			{showProfileChooser && selectedProfile && (
				<div className="flex flex-col gap-1">
					<span className="text-xs font-medium text-grey-500">
						{t('billingDrawer.profileLabel')}
					</span>
					<ProfilePicker
						profiles={profiles}
						selected={selectedProfile}
						disabled={isSubmitting}
						onSelect={(profile) => {
							setSelectedProfileId(profile.id)
							setSubmitError(null)
						}}
						t={t}
					/>
				</div>
			)}

			<div className="border-t border-grey-100" />

			<div className="flex flex-col gap-2">
				<div className="flex justify-between">
					<span className="text-grey-500">{t('completePopup.budget')}</span>
					<span className="text-grey-900 tabular-nums">&euro;{budget.toFixed(2)}</span>
				</div>
				{selectedAddons.map((addon) => (
					<div key={addon.id} className="flex justify-between">
						<span className="text-grey-500">{addon.name}</span>
						<span className="text-grey-900 tabular-nums">
							&euro;{centsToEuros(addon.price!).toFixed(2)}
						</span>
					</div>
				))}
				{inventoryAllocation.topUpCents > 0
					? (
						<div className="flex justify-between">
							<span className="text-grey-500">{t('billingDrawer.walletTopUp')}</span>
							<span className="text-grey-900 tabular-nums">&euro;{walletTopUp.toFixed(2)}</span>
						</div>
					)
					: null}

				<div className="border-t border-grey-100 my-1" />

				<div className="flex justify-between">
					<span className="text-grey-500">{t('completePopup.subtotal')}</span>
					<span className="text-grey-700 tabular-nums">&euro;{subtotal.toFixed(2)}</span>
				</div>
				<div className="flex justify-between">
					<span className="text-grey-500">{t('completePopup.vat', { percent: vatPercent })}</span>
					<span className="text-grey-700 tabular-nums">&euro;{vatAmount.toFixed(2)}</span>
				</div>

				<div className="border-t border-grey-100 my-1" />

				<div className="flex justify-between font-semibold text-grey-900">
					<span>{t('completePopup.total')}</span>
					<span className="tabular-nums">&euro;{total.toFixed(2)}</span>
				</div>
			</div>

			{totalPayments > 0 && (
				<>
					<div className="border-t border-grey-100 my-1" />
					<div className="text-xs font-medium text-grey-500">
						{t('completePopup.paymentsReceived')}
					</div>
					{successfulPayments.map((payment) => (
						<div key={payment.id} className="flex justify-between text-emerald-600">
							<span>{t('completePopup.payment')}</span>
							<span className="tabular-nums">-&euro;{centsToEuros(payment.amount).toFixed(2)}</span>
						</div>
					))}
				</>
			)}

			{canUseInventory && hasBillableChannel && selectableInventory.length > 0
				? (
					<div className="mt-1 flex flex-col gap-3">
						<div>
							<h3 className="text-base font-semibold text-grey-900">
								{t('billingDrawer.contractTermsHeading')}
							</h3>
							<p className="text-sm text-grey-500 mt-0.5">
								{t('billingDrawer.contractTermsHelp')}
							</p>
						</div>
						<InventoryTile
							appliedCents={inventoryAllocation.appliedCents}
							onToggle={() => {
								setSubmitError(null)
								if (inventoryAllocation.appliedCents > 0) {
									setInventorySelection({})
								} else {
									setIsInventoryModalOpen(true)
								}
							}}
							onChange={() => setIsInventoryModalOpen(true)}
							currencyFormatter={currencyFormatter}
							t={t}
						/>
					</div>
				)
				: null}

			{(walletAvailable || (requiresPayment && paymentOptions.length > 0)) && (
				<div className="mt-1 flex flex-col gap-3">
					<div>
						<h3 className="text-base font-semibold text-grey-900">
							{t('billingDrawer.paymentMethodHeading')}
						</h3>
						<p className="text-sm text-grey-500 mt-0.5">
							{t('billingDrawer.paymentMethodHelp')}
						</p>
					</div>
					{walletAvailable && (
						<WalletTile
							useWallet={useWallet}
							onToggle={() => {
								setUseWallet(!useWallet)
								setSubmitError(null)
							}}
							appliedAmount={walletApplicable}
							isBlockedByInventory={isInventoryApplied}
							currencyFormatter={currencyFormatter}
							t={t}
						/>
					)}
					{requiresPayment && paymentOptions.length > 0 && (
						<PaymentMethodPicker
							options={paymentOptions}
							selected={selected}
							onSelect={handlePickPaymentOption}
							paymentTerms={paymentTerms}
							t={t}
						/>
					)}
				</div>
			)}

			{externalInvoicing && (
				<div className="mt-1 p-3 bg-indigo-50 border border-indigo-100 rounded-lg text-xs text-indigo-800 leading-relaxed">
					{t('billingDrawer.externalInvoicing')}
				</div>
			)}

			<div className="mt-2">
				<Button
					type="green"
					postfix="arrow"
					postfixAlign="right"
					animatePostfix="shake"
					fullWidth
					onClick={handleConfirm}
					disabled={!canPay}
					isLoading={isSubmitting}
				>
					<div className="flex-1 text-center pl-10 text-base font-semibold">
						{buttonHeading}
					</div>
				</Button>
				{submitError
					? <p className="mt-2 text-sm text-red-600 text-center">{submitError}</p>
					: null}
			</div>

			<InventoryModal
				open={isInventoryModalOpen}
				items={selectableInventory}
				selection={inventorySelection}
				campaignCostCents={campaignCostCents}
				currencyFormatter={currencyFormatter}
				onClose={() => setIsInventoryModalOpen(false)}
				onApply={(nextSelection) => {
					setInventorySelection(nextSelection)
					setIsInventoryModalOpen(false)
				}}
			/>
		</aside>
	)
}

type TFn = (key: string, opts?: Record<string, unknown>) => string

function paymentOptionTitle(option: PaymentOption, t: TFn): string {
	if (option.kind === 'invoice') {
		return t('billingDrawer.invoiceOption')
	}
	return option.nickname ?? cardBrandName(option.brand)
}

function paymentOptionSubtitle(option: PaymentOption, t: TFn): string | null {
	if (option.kind === 'invoice') {
		return null
	}
	return t('billingDrawer.cardDetails', {
		last4: option.last4,
		month: String(option.expMonth).padStart(2, '0'),
		year: String(option.expYear % 100).padStart(2, '0'),
	})
}

function PaymentTermsBadge({ paymentTerms, t }: { paymentTerms: number; t: TFn }) {
	return (
		<div className="flex flex-col items-end leading-tight shrink-0">
			<span className="text-[10px] font-medium text-grey-500 uppercase tracking-wider">
				{t('common:settings.invoices.paymentTerms.label')}
			</span>
			<span className="text-sm font-semibold text-grey-900 tabular-nums">
				{t('common:settings.invoices.paymentTerms.value', { count: paymentTerms })}
			</span>
		</div>
	)
}

type SelectionIndicator = 'checkbox' | 'radio' | 'none'

function SelectionTile(
	{ checked, onClick, title, subtitle, rightSlot, indicator = 'checkbox', highlight = true, disabled = false }: {
		checked: boolean
		onClick?: () => void
		title: string
		subtitle?: string | null
		rightSlot?: ReactNode
		indicator?: SelectionIndicator
		highlight?: boolean
		disabled?: boolean
	},
) {
	const isHighlighted = highlight && checked
	const handleClick = disabled ? undefined : onClick
	return (
		<div
			role={indicator === 'radio' ? 'radio' : 'checkbox'}
			aria-checked={checked}
			aria-disabled={disabled}
			tabIndex={disabled ? -1 : 0}
			onClick={handleClick}
			onKeyDown={(event) => {
				if (event.key === ' ' || event.key === 'Enter') {
					event.preventDefault()
					handleClick?.()
				}
			}}
			className={`flex items-center gap-3 p-3 border rounded-lg transition-all ${
				disabled
					? 'border-grey-200 bg-grey-50 opacity-60 cursor-not-allowed'
					: isHighlighted
					? 'border-brand-500 bg-brand-50 ring-1 ring-brand-500/30 cursor-pointer'
					: 'border-grey-200 bg-white hover:border-grey-300 hover:bg-grey-50 cursor-pointer'
			}`}
		>
			{indicator === 'checkbox' && (
				<input
					type="checkbox"
					className="accent-brand-500 shrink-0 pointer-events-none"
					checked={checked}
					disabled={disabled}
					readOnly
					tabIndex={-1}
				/>
			)}
			{indicator === 'radio' && (
				<input
					type="radio"
					className="accent-brand-500 shrink-0 pointer-events-none"
					checked={checked}
					readOnly
					tabIndex={-1}
				/>
			)}
			<div className="flex flex-col leading-tight min-w-0 flex-1">
				<span className="text-base font-semibold text-grey-900 truncate">{title}</span>
				{subtitle && <span className="text-xs text-grey-500 tabular-nums">{subtitle}</span>}
			</div>
			{rightSlot && <div className="shrink-0">{rightSlot}</div>}
		</div>
	)
}

function profileLabel(profile: BillingProfileData): string {
	return profile.name ?? profile.stripeCustomerId
}

type ProfilePickerProps = {
	profiles: BillingProfileData[]
	selected: BillingProfileData
	disabled: boolean
	onSelect: (profile: BillingProfileData) => void
	t: TFn
}

// Mirrors PaymentMethodPicker: a plain click-driven dropdown rather than a
// Headless Listbox, because this drawer renders outside the Headless DialogPanel
// and the dialog's focus trap stops a focus-driven listbox from opening.
function ProfilePicker({ profiles, selected, disabled, onSelect, t }: ProfilePickerProps) {
	const [open, setOpen] = useState(false)
	const containerRef = useRef<HTMLDivElement>(null)

	return (
		<div
			ref={containerRef}
			className="flex flex-col gap-2"
			onBlur={(e) => {
				if (!containerRef.current?.contains(e.relatedTarget)) {
					setOpen(false)
				}
			}}
		>
			<SelectionTile
				checked={!open}
				indicator="none"
				onClick={disabled ? undefined : () => setOpen(!open)}
				title={profileLabel(selected)}
				subtitle={selected.isPrimary ? t('common:profiles.primary') : null}
				rightSlot={
					<ChevronDownIcon
						className={`size-4 text-grey-500 transition-transform ${open ? 'rotate-180' : ''}`}
					/>
				}
			/>

			{open && (
				<div className="flex flex-col gap-2">
					{profiles.map((profile) => (
						<SelectionTile
							key={profile.id}
							checked={profile.id === selected.id}
							indicator="radio"
							highlight={false}
							onClick={() => {
								onSelect(profile)
								setOpen(false)
							}}
							title={profileLabel(profile)}
							subtitle={profile.isPrimary ? t('common:profiles.primary') : null}
						/>
					))}
				</div>
			)}
		</div>
	)
}

type PaymentMethodPickerProps = {
	options: PaymentOption[]
	selected: PaymentOption | null
	onSelect: (option: PaymentOption) => void
	paymentTerms: number | null
	t: TFn
}

function PaymentMethodPicker({ options, selected, onSelect, paymentTerms, t }: PaymentMethodPickerProps) {
	const [open, setOpen] = useState(false)
	const containerRef = useRef<HTMLDivElement>(null)
	const isOnlyOption = options.length === 1
	const display = selected ?? options[0]!

	return (
		<div
			ref={containerRef}
			className="flex flex-col gap-2"
			onBlur={(e) => {
				if (!containerRef.current?.contains(e.relatedTarget)) {
					setOpen(false)
				}
			}}
		>
			<SelectionTile
				checked={!open}
				indicator="none"
				onClick={isOnlyOption ? undefined : () => setOpen(!open)}
				title={paymentOptionTitle(display, t)}
				subtitle={paymentOptionSubtitle(display, t)}
				rightSlot={
					<div className="flex items-center gap-3">
						{display.kind === 'invoice' && paymentTerms !== null && (
							<PaymentTermsBadge paymentTerms={paymentTerms} t={t} />
						)}
						{!isOnlyOption && (
							<ChevronDownIcon
								className={`size-4 text-grey-500 transition-transform ${open ? 'rotate-180' : ''}`}
							/>
						)}
					</div>
				}
			/>

			{open && !isOnlyOption && (
				<div className="flex flex-col gap-2">
					{options.map((option) => {
						const isCurrent = selected !== null
							&& paymentOptionKey(option) === paymentOptionKey(selected)
						return (
							<SelectionTile
								key={paymentOptionKey(option)}
								checked={isCurrent}
								indicator="radio"
								highlight={false}
								onClick={() => {
									onSelect(option)
									setOpen(false)
								}}
								title={paymentOptionTitle(option, t)}
								subtitle={paymentOptionSubtitle(option, t)}
								rightSlot={option.kind === 'invoice' && paymentTerms !== null
									? <PaymentTermsBadge paymentTerms={paymentTerms} t={t} />
									: null}
							/>
						)
					})}
				</div>
			)}
		</div>
	)
}

type WalletTileProps = {
	useWallet: boolean
	onToggle: () => void
	appliedAmount: number
	isBlockedByInventory: boolean
	currencyFormatter: Intl.NumberFormat
	t: TFn
}

function WalletTile(
	{ useWallet, onToggle, appliedAmount, isBlockedByInventory, currencyFormatter, t }: WalletTileProps,
) {
	if (isBlockedByInventory) {
		return (
			<SelectionTile
				checked={false}
				disabled={true}
				title={t('billingDrawer.walletOption')}
				subtitle={t('billingDrawer.walletUnavailableWithInventory')}
			/>
		)
	}

	return (
		<SelectionTile
			checked={useWallet}
			onClick={onToggle}
			title={t('billingDrawer.walletOption')}
			subtitle={useWallet
				? t('billingDrawer.walletApplied', { amount: currencyFormatter.format(appliedAmount) })
				: t('billingDrawer.walletAvailable', { amount: currencyFormatter.format(appliedAmount) })}
		/>
	)
}

type InventoryTileProps = {
	appliedCents: number
	onToggle: () => void
	onChange: () => void
	currencyFormatter: Intl.NumberFormat
	t: TFn
}

function InventoryTile({ appliedCents, onToggle, onChange, currencyFormatter, t }: InventoryTileProps) {
	const isApplied = appliedCents > 0

	return (
		<SelectionTile
			checked={isApplied}
			onClick={onToggle}
			title={t('billingDrawer.inventoryOption')}
			subtitle={isApplied
				? t('billingDrawer.inventoryApplied', { amount: currencyFormatter.format(centsToEuros(appliedCents)) })
				: t('billingDrawer.inventoryNoneApplied')}
			rightSlot={isApplied
				? (
					<button
						type="button"
						onClick={(event) => {
							event.stopPropagation()
							onChange()
						}}
						onKeyDown={(event) => event.stopPropagation()}
						className="px-2 py-1.5 rounded-md text-[13px] font-semibold text-brand-700 hover:bg-brand-100 hover:text-brand-900 cursor-pointer"
					>
						{t('billingDrawer.inventoryChange')}
					</button>
				)
				: null}
		/>
	)
}
